import React, {useCallback} from 'react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {useHasReportRuns} from '../../state/runs/context';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../state/views/runSet/types';
import {observeMetricSelectionPerfWindow} from '../../util/datadog/criticalWorkflows/perfWindow/metricSelection';
import {getUpdatedDropdownMetrics} from '../elements/MetricsPickerDropdown';
import {
  type MetricsPickerConfig,
  MetricsRegexToggle,
} from '../elements/MetricsPickerShared';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {type RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {MetricSearchDropdown} from './MetricSearchDropdown';
import {MetricsRegexInput} from './MetricsRegexInput';

// Number-typed keys that should not be offered as a y-axis (removed client-side
// since metricSearch has no per-key blocklist).
const Y_AXIS_BLOCKLIST = new Set(['_absolute_runtime', '_timestamp']);
const EMPTY_METRICS: string[] = [];

// Stable `types` arrays (used as a hook dependency, so identity must be stable).
const Y_AXIS_METRIC_TYPES = ['number'];
const Y_AXIS_METRIC_TYPES_WITH_HISTOGRAM = ['number', 'histogram'];

type YAxisMetricPickerProps = {
  runSetRefs: RunSetTypes.Ref[];
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined;
  config: MetricsPickerConfig;
  updateConfig: (arg: Partial<RunsLinePlotConfig>) => void;
};

/**
 * NGW y-axis metric picker. Renders regex mode (`MetricsRegexInput`) or the
 * multi-select `MetricSearchDropdown` + regex toggle, based on
 * `config.useMetricRegex`. Owns the y-axis-specific selection policy (histograms
 * can't coexist with other metrics) so that rule stays out of the generic
 * dropdown.
 */
export const YAxisMetricPicker = ({
  runSetRefs,
  customRunNamesRef,
  config,
  updateConfig,
}: YAxisMetricPickerProps) => {
  const {isSingleRun} = useSingleRunModeContext();
  const hasRunsReport = useHasReportRuns();
  // Histograms are only offered when they can render full-fidelity.
  const isHistogramAllowed = isSingleRun || hasRunsReport;
  const trackSettingPage = useTrackSettingPage();

  const onChange = useCallback(
    (
      event: React.SyntheticEvent<HTMLElement>,
      {
        value,
        histogramKeys,
      }: {value?: string | string[]; histogramKeys: Set<string>}
    ) => {
      if (!Array.isArray(value) || !value.every(k => typeof k === 'string')) {
        return;
      }
      // Histograms can't coexist with other metrics: if the selection includes
      // one, collapse to just the newest pick
      const nextMetrics = getUpdatedDropdownMetrics(value, k =>
        histogramKeys.has(k)
      );
      observeMetricSelectionPerfWindow({
        axis: 'y',
        event,
        initialSelection: config.metrics ?? EMPTY_METRICS,
        nextSelection: nextMetrics,
        pickerVariant: 'metric_search',
      });
      updateConfig({metrics: nextMetrics});

      trackSetting({
        action: 'change',
        page: trackSettingPage,
        settingLevel: 'panel',
        setting: 'yAxis',
        settingValue: nextMetrics.toString(),
        additionalProperties: {
          metrics: nextMetrics,
        },
      });
    },
    [config.metrics, updateConfig, trackSettingPage]
  );

  if (config.useMetricRegex) {
    // Regex mode: server-side RE2 match resolves the plotted metrics.
    return (
      <MetricsRegexInput
        runSetRefs={runSetRefs}
        customRunNamesRef={customRunNamesRef}
        config={config}
        updateConfig={updateConfig}
      />
    );
  }

  return (
    <div className="metrics-picker">
      <div className="metrics-picker-control-wrapper">
        <MetricSearchDropdown
          perfAction="edit-panel-y-metric-picker"
          multiple
          className="regex-dropdown"
          dataTest="y-axis"
          types={
            isHistogramAllowed
              ? Y_AXIS_METRIC_TYPES_WITH_HISTOGRAM
              : Y_AXIS_METRIC_TYPES
          }
          excludeKeys={Y_AXIS_BLOCKLIST}
          value={config.metrics ?? EMPTY_METRICS}
          onChange={onChange}
          placeholder="Select metrics to visualize"
          controlOverlay={
            <div className="metrics-picker-dropdown-actions">
              <MetricsRegexToggle config={config} updateConfig={updateConfig} />
            </div>
          }
        />
      </div>
    </div>
  );
};
